import { useSupabase } from '@packages/supabase'
import { useCallback, useMemo, useState } from 'react'
import {
  sendCirclePasswordReset,
  sendSupabasePasswordReset
} from '../passwordReset'
import { getAuthRedirectUrl } from '../redirect'
import type {
  ForgotPasswordActions,
  ForgotPasswordContext,
  ForgotPasswordState,
  UseForgotPasswordOptions
} from '../types'

const defaultValidateEmail = (email: string) => {
  if (email.length < 4 || !email.includes('@')) {
    return 'Please enter a valid email'
  }
  return null
}

export function useForgotPassword(
  options: UseForgotPasswordOptions
): ForgotPasswordContext {
  const { supabase } = useSupabase()
  const {
    email,
    setEmail,
    strategy = 'supabase',
    redirectTo = getAuthRedirectUrl(),
    language,
    validateEmail = defaultValidateEmail
  } = options

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetEmailSent, setResetEmailSent] = useState(false)

  const submit = useCallback(
    async (event?: { preventDefault?: () => void }) => {
      event?.preventDefault?.()

      const validationError = validateEmail(email)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      setLoading(true)

      const result =
        strategy === 'circle'
          ? await sendCirclePasswordReset(supabase, email, language)
          : await sendSupabasePasswordReset(supabase, email, redirectTo)

      setLoading(false)

      if (result.success) {
        setResetEmailSent(true)
      } else {
        setError(result.error ?? 'Failed to send reset email')
      }
    },
    [email, strategy, supabase, redirectTo, language, validateEmail]
  )

  const state = useMemo<ForgotPasswordState>(
    () => ({
      email,
      loading,
      error,
      resetEmailSent
    }),
    [email, loading, error, resetEmailSent]
  )

  const actions = useMemo<ForgotPasswordActions>(
    () => ({
      setEmail,
      submit,
      clearError: () => setError(null),
      resetSentState: () => setResetEmailSent(false)
    }),
    [setEmail, submit]
  )

  return { state, actions }
}
